import { useEffect, useRef, useState } from "react";
/**
 * Tracks which direct child of `containerRef` is currently most visible
 * inside the container's own scroll viewport. Useful for scroll-snap
 * carousels, scrollspy navigation, etc.
 *
 * Returns the index of the visible child with the highest intersection
 * ratio (ties broken by document order). Stays at the last known index
 * when no child currently meets the threshold.
 */
export function useVisibleChildIndex(containerRef, options = {}) {
  const [index, setIndex] = useState(0);
  const optionsRef = useRef(options);
  optionsRef.current = options;
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const {
      selector,
      threshold = [0.55, 0.75, 1],
      rootMargin,
      minRatio = 0.55
    } = optionsRef.current;
    const children = selector ? Array.from(container.querySelectorAll(`:scope > ${selector}`)) : Array.from(container.children);
    if (children.length === 0) return;
    const indexByElement = new Map(children.map((el, i) => [el, i]));
    const init = {
      root: container,
      threshold
    };
    if (rootMargin !== undefined) init.rootMargin = rootMargin;

    // An observer batch only holds the children whose ratio *changed*, so the latest ratio of every child is kept
    // here and the winner is picked across all of them. Picking from the batch alone breaks when several children
    // are fully visible (e.g. a two-up carousel): only the newcomer is reported, so the index jumps ahead.
    const ratios = children.map(() => 0);
    const observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        const idx = indexByElement.get(entry.target);
        if (idx !== undefined) ratios[idx] = entry.intersectionRatio;
      }
      let bestIdx = -1;
      let bestRatio = -1;
      // Strictly greater, scanning in document order, so ties go to the earliest child.
      ratios.forEach((ratio, idx_0) => {
        if (ratio >= minRatio && ratio > bestRatio) {
          bestRatio = ratio;
          bestIdx = idx_0;
        }
      });
      if (bestIdx !== -1) setIndex(bestIdx);
    }, init);
    children.forEach(el_0 => observer.observe(el_0));
    return () => observer.disconnect();
  }, [containerRef]);
  return index;
}